import {
  simplifyTechnologies,
  resolveProductEnrichments,
  type Product,
  type ProductType,
  type ProductRating,
  type ProductEnrichmentsDefinition,
  type Range,
  type ProductHighlightLabel,
  type ProductBundle,
  type BundleContents,
  excludeByProductBTGRules,
  excludeByCustomerSegments,
  type ProductMetadata
} from '@/domain/Product.ts';
import {
  getByDurationForProduct,
  type Subscription
} from '@/domain/Subscription.ts';
import { type Pricing } from '@/domain/Pricing.ts';
import type {
  ProductDTO,
  ProductTypeDTO,
  HierarchicalCategoryDTO
} from '@/services/generated/ECAPIProductsDTOs.ts';
import { getPromotionsForProduct, type PromotionGroup } from '../Promotion.ts';
import { fromEcapiDto as assembleProductSpecifications } from './SpecificationsAssembler.ts';
import decodeCategories from '@/services/utils/decodeCategories.ts';
import type { Category } from '../Category.ts';
import { type I18NInstance } from '@/utils/createI18nInstance.ts';
import { type ProductTranslations } from '../config/index.ts';
import type { SpecificProductsResponseDTO } from '@/services/generated/ECAPICustomersDTOs.ts';
import type { UserGroupsDTO } from '@/widgets/services/EcommerceService.ts';

export type PricesMap = Map<string, Pricing | undefined>;

export type CustomerProductRules = {
  selections?: SpecificProductsResponseDTO['selections'];
  segments?: UserGroupsDTO['userGroups'];
};

export function fromEcapiDto(
  productDto: any,
  prices: PricesMap,
  subscriptions: Subscription[],
  rangesMap: Map<string, Set<string>>,
  promotions: PromotionGroup[],
  enrichments: ProductEnrichmentsDefinition,
  categories: HierarchicalCategoryDTO[],
  modelsToRanges: Map<string, Set<string>>,
  stocks: Map<string, boolean>,
  productI18nInstance: I18NInstance<ProductTranslations>,
  rating?: ProductRating,
  productDtos?: Map<string, any>
): Product {
  const rangeCategory = findRangeCategory(productDto) ?? '';
  const modelCategory = findModelCategory(
    rangeCategory,
    productDto.type,
    categories
  );

  const pricing = prices.get(productDto.id) || {
    price: 0,
    pricePerCapsule: 0,
    pricePerSleeve: 0,
    pricePerWeight: 0
  };
  const inStock = stocks.get(productDto.id) ?? true; // Fail safe on missing stock data

  const altImageStr = productDto.name;

  const mainImage = {
    alt: altImageStr,
    url: productDto.responsiveImages?.standard ?? ''
  };

  const cardImageUrl =
    productDto.responsiveImages?.plp ??
    productDto.responsiveImages?.standard ?? // Fail safe on missing PLP image
    '';

  const cardImage = {
    alt: altImageStr,
    url: cardImageUrl
  };

  const media = (productDto?.mediaGallery ?? []).map(
    (m: { url: string; altText?: string; type: string }) => ({
      url: m.url,
      altText: m.altText ?? '',
      type: m.type
    })
  );

  const technologies = simplifyTechnologies(productDto.technologies);
  const productPromotions = getPromotionsForProduct(promotions, productDto.id);

  const productEnrichments = resolveProductEnrichments(
    enrichments,
    technologies[0],
    productDto.type as unknown as ProductType,
    decodeCategories(productDto.supercategories),
    productDto.id
  );

  const specifications = assembleProductSpecifications(
    productDto,
    categories,
    productI18nInstance
  );

  // TODO: Use this Resolver pattern to simplify the ProductAssembler
  const rangeProductsResolver = (cat: string) =>
    productDtos
      ? findRangeProducts(
          cat,
          prices,
          subscriptions,
          rangesMap,
          promotions,
          enrichments,
          stocks,
          productDtos,
          productI18nInstance
        )
      : [];

  const bundledProductsResolver = (): ProductBundle | undefined => {
    const {
      bundleContents = [],
      isAssortment = false,
      soldSeparatelyPrice = ''
    } = productEnrichments;

    return findBundleContent(
      bundleContents,
      prices,
      subscriptions,
      rangesMap,
      promotions,
      enrichments,
      stocks,
      isAssortment,
      soldSeparatelyPrice,
      productI18nInstance,
      productDtos
    );
  };

  const findSubscriptionForProduct = (
    subscriptionsArg: Subscription[],
    productDtoArg: any
  ): Subscription | undefined =>
    subscriptionsArg.find((sub) =>
      sub.productIds?.includes(productDtoArg.legacyId)
    );

  const rangeProducts = rangeProductsResolver(rangeCategory);

  const resolvedBundle: ProductBundle | undefined = productEnrichments
    ? bundledProductsResolver()
    : undefined;
  const bundle: ProductBundle | undefined = productDto.bundled
    ? (resolvedBundle ?? {
        contents: [] // configurator needs to specify bundle contents
      })
    : undefined;

  const output = {
    id: productDto.id,
    name: productDto.name.trim(),
    slug: productDto.urlFriendlyName,
    description: productDto.description ?? '',
    headline: productDto.headline ?? '',
    type: productDto.type as unknown as ProductType,
    technologies,
    slides: productDto.slides,
    pricing,
    inStock,
    isOrderable: productDto.isOrderable,
    // Note: Usually it's set to zero meaning no limit (better undefined for us)
    maxOrderQuantity: productDto.maxOrderQuantity || undefined,
    quantities: productDto.quantities,
    productSelections: productDto.productSelections,
    segments: productDto.segments,
    salesMultiple: productDto.salesMultiple,
    unitQuantity: productDto.unitQuantity,
    bundle,
    categories: resolveCategories(productDto.supercategories, categories),
    range: {
      category: rangeCategory,
      products: rangeProducts
    },
    model: modelCategory
      ? {
          category: modelCategory,
          ranges: findModelRanges(
            modelCategory,
            modelsToRanges,
            rangeProductsResolver
          )
        }
      : undefined,
    color: productDto.colorShade
      ? {
          name: productDto.colorShade.name,
          value: productDto.colorShade.cssCode
        }
      : undefined,
    rating,
    subscriptionsByDuration: getByDurationForProduct(subscriptions, {
      id: productDto.id,
      isSubscriptionOnly: productEnrichments.isSubscriptionOnly
    }),
    subscriptionsInfo: findSubscriptionForProduct(subscriptions, productDto),
    mainImage,
    cardImage,
    media,
    promotions: productPromotions,
    ...productEnrichments,
    nessoft: {
      internationalId: productDto.internationalId,
      legacyId: productDto.legacyId,
      internationalName: productDto.internationalName,
      category: productDto.category
    },
    seo: productDto.seo
      ? {
          description: productDto.seo.description
        }
      : {},
    specifications,
    highlightLabels: mapHighlightLabels(productDto),
    originDescription: productDto.originDescription ?? '',
    roastingDescription: productDto.roastingDescription ?? '',
    displayEcoTax: productDto.displayEcoTax
  };

  return output;
}

export function enrichmentsFromEcapiDto(
  config: any
): ProductEnrichmentsDefinition {
  return config;
}

export function createProductMaps(
  productsByCategories: any[],
  categories: HierarchicalCategoryDTO[],
  customerProductRules?: CustomerProductRules
): [
  Map<Product['id'], ProductDTO>,
  Map<Product['id'], Set<string>>,
  Map<Product['id'], Set<string>>,
  Map<Product['id'], ProductMetadata>
] {
  const products = new Map<Product['id'], ProductDTO>();
  const ranges = new Map<Product['id'], Set<string>>();
  const models = new Map<Product['id'], Set<string>>();
  const metaData = new Map<Product['id'], ProductMetadata>();

  let listPosition = 0;

  for (const category of productsByCategories) {
    let currentCategoryListPosition = 0;

    for (const product of category.products) {
      if (!product.urlFriendlyName) {
        // eslint-disable-next-line no-continue
        continue;
      }
      if (
        excludeByProductBTGRules(
          product.productSelections,
          customerProductRules?.selections
        ) ||
        excludeByCustomerSegments(
          product.segments ?? undefined,
          customerProductRules?.segments
        )
      )
        break;

      products.set(product.id, product);

      // Store general position
      if (!metaData.has(product.id)) {
        metaData.set(product.id, {
          listPosition: ++listPosition,
          categoryListPosition: {}
        });
      }

      // Store position against collection
      metaData.get(product.id)!.categoryListPosition[category.code] =
        ++currentCategoryListPosition;

      const range = findRangeCategory(product);
      if (range) {
        if (!ranges.has(range)) {
          ranges.set(range, new Set());
        }
        ranges.get(range)?.add(product.id);

        const modelCategory = findModelCategory(
          range,
          product.type,
          categories
        );
        if (modelCategory) {
          if (!models.has(modelCategory)) {
            models.set(modelCategory, new Set());
          }
          models.get(modelCategory)?.add(range);
        }
      }
    }
  }

  return [products, ranges, models, metaData];
}

function findRangeCategory(product: ProductDTO): string | undefined {
  return decodeCategories(product.supercategories).find((cat) =>
    isRangeCategory(cat, product.type)
  );
}

function findRangeProducts(
  rangeCategory: string,
  prices: PricesMap,
  subscriptions: Subscription[],
  rangesMap: Map<string, Set<string>>,
  promotions: PromotionGroup[],
  enrichments: ProductEnrichmentsDefinition,
  stocks: Map<string, boolean>,
  productDtos: Map<string, any>,
  productI18nInstance: I18NInstance<ProductTranslations>
): Product[] {
  const rangeProductIds = [...(rangesMap.get(rangeCategory) ?? [])];

  const rangeProductDtos = rangeProductIds
    .map((productId) => productDtos.get(productId))
    .filter((dto) => dto !== undefined);

  return rangeProductDtos.map((productDto) =>
    fromEcapiDto(
      productDto,
      prices,
      subscriptions,
      rangesMap,
      // bundles,
      promotions,
      enrichments,
      [],
      new Map(),
      stocks,
      productI18nInstance
    )
  );
}

// TODO: This should use the categories data
function isRangeCategory(
  category: string,
  productType: ProductTypeDTO
): boolean {
  const parts = category.split('/');
  if (parts[1] !== 'cat') return false;

  if (!parts[2].startsWith(`${productType}-range-`)) return false;

  return true;
}

// TODO: This should use the categories data
function isModelCategory(
  category: string,
  productType: ProductTypeDTO
): boolean {
  const parts = category.split('/');
  if (parts[1] !== 'cat') return false;

  if (!parts[2].startsWith(`${productType}-model-`)) return false;

  return true;
}

function findModelCategory(
  rangeCategory: string,
  productType: ProductTypeDTO,
  categories: HierarchicalCategoryDTO[] = []
): string | undefined {
  for (const cat of categories) {
    if (isModelCategory(cat.id, productType)) {
      const match = cat.subCategories?.includes(rangeCategory);
      if (match) {
        return cat.id;
      }
    }
  }

  return undefined;
}

export function findModelRanges(
  modelCategory: string,
  modelsToRangeMap: Map<string, Set<string>>,
  resolveRangeProducts: (rangeCategory: string) => Product[]
): Range[] {
  const ranges = modelsToRangeMap.get(modelCategory);
  return [...(ranges?.values() ?? [])].map((rangeCategory) => ({
    category: rangeCategory,
    products: resolveRangeProducts(rangeCategory)
  }));
}

function resolveCategories(
  superCategories: string[] = [],
  categories: HierarchicalCategoryDTO[] = []
): Map<string, Category> {
  return new Map(
    decodeCategories(superCategories)
      .map((cat) => {
        const category = categories.find((item) => item.id === cat);

        if (!category) return [undefined, undefined];

        return [
          category.code,
          {
            id: cat,
            code: category.code,
            name: category.name,
            description: category?.description ?? ''
          }
        ];
      })
      .filter(([code]) => code !== undefined) as [[string, Category]]
  );
}

function mapHighlightLabels(productDto: any): ProductHighlightLabel[] {
  return productDto.highlightLabels.map((label: any) => ({
    id: label.id ?? label.text ?? '',
    label: label.text ?? '',
    bgColor: label.backgroundColor ?? '',
    fgColor: label.textColor ?? ''
  }));
}

function findBundleContent(
  bundleContents: BundleContents[],
  prices: PricesMap,
  subscriptions: Subscription[],
  rangesMap: Map<string, Set<string>>,
  promotions: PromotionGroup[],
  enrichments: ProductEnrichmentsDefinition,
  stocks: Map<string, boolean>,
  isAssortment: boolean,
  soldSeparatelyPrice: string,
  productI18nInstance: I18NInstance<ProductTranslations>,
  productDtos?: Map<string, any>
): ProductBundle | undefined {
  if (!productDtos || bundleContents.length === 0) {
    return undefined;
  }
  const contents: ProductBundle['contents'] = bundleContents.map((item) => {
    const productDto = productDtos.get(item.productId);
    if (!productDto)
      return {
        id: item.productId,
        quantityInBundle: item.quantity
      };

    const product = fromEcapiDto(
      productDto,
      prices,
      subscriptions,
      rangesMap,
      promotions,
      enrichments,
      [],
      new Map(),
      stocks,
      productI18nInstance
    );
    return {
      ...product,
      quantityInBundle: item.quantity
    };
  });

  return {
    isAssortment,
    soldSeparatelyPrice,
    contents
  };
}
